'use client';

import type { I18n } from '@funkey/locales';
import { useMemo } from 'react';
import { std, type ts } from '@funkey/shared';
import _useFormState, { createFormValidation } from 'use-form-state';
import { useI18n } from '~/features/i18n';
import useNotifications from '~/features/notifications/useNotifications';
import type { SingleOrArray } from '~/utils/types';

type ValidationConfig<T, K extends keyof T, Options = never> = {
    validate: (value: T[K], allValues: T, options: Options) => boolean;
    message: string;
};

export type ValidationConfigMap<T extends Record<string, unknown>, Options = never> = {
    [K in Extract<keyof T, string>]?: SingleOrArray<ValidationConfig<T, K, Options>> | undefined;
};

export function useFormState<
    T extends Record<string, any>,
    ValidationOptions extends Record<string, unknown>,
>(
    initialValues: T,
    validation: ValidationConfigMap<T, NoInfer<ValidationOptions>>,
    options: {
        validationOptions?: ValidationOptions;
        valuesToInput?(formValues: T): ValidationOptions;
    },
): FormState<T>;
export function useFormState<T extends Record<string, any>>(
    initialValues: T,
    validation?: ValidationConfigMap<T, never>,
): FormState<T>;
export function useFormState<T extends Record<string, any>>(
    initialValues: T,
    validation?: ValidationConfigMap<T, never>,
    options: {
        validationOptions?: any;
        valuesToInput?(formValues: T): unknown;
    } = {},
): FormState<T> {
    const formState = _useFormState(initialValues, {
        validation: createFormValidation(
            validation === undefined
                ? []
                : std.Object.entries(
                      validation as Record<string, FormState.Validation.Entry<any, any>>,
                  )
                      .flatMap(([path, config]) =>
                          Array.isArray(config)
                              ? config.map(
                                    x => [path, x as ValidationConfig<any, any, any>] as const,
                                )
                              : [[path, config as ValidationConfig<any, any, any>] as const],
                      )
                      .map(([path, config]) => ({
                          ...config,
                          message: config.message,
                          path,
                      })),
        ),
        validationOptions: options.validationOptions,
        valuesToInput: options.valuesToInput,
    });

    const sdk = {
        ...formState,
        handleChange<K extends keyof T>(key: K, value: React.SetStateAction<T[K]>) {
            formState.handleChange(
                key,
                typeof value === 'function'
                    ? (value as (prevState: T[K]) => T[K])(formState.values[key])
                    : value,
            );
        },

        /** Casts the this `FormState` to a `FormState.Subform<Subset>`.
         *
         * This is useful when you're composing multiple subform components that all receive the same `FromState` instance
         *
         * All subform components can own their own required types without having to know about the full form state.
         *
         * # Example
         * ```typescript
         * const CRUD_Page = props => {
         *    const form = useFormState<
         *        | Form1.FormValues
         *        | Form2.FormValues
         *        | Form3.FormValues
         *        | ...
         *    >({ ... })
         *
         *    // ...
         *
         *    return <>
         *        <Form1 form={form.$subform<From1.FormValues>()}
         *        <Form2 form={form.$subform<From2.FormValues>()}
         *        <Form3 form={form.$subform<From3.FormValues>()}
         *        { ... }
         *        <button onClick={onSubmit} />
         *    </>
         * }
         *
         * namespace Form1 {
         *    export type FormValues = { field1: string, ... };
         * }
         * const Form1: React.FC<{ form: FormState.Subform<Form1.FormValues> }> = props => <> ... </>
         *
         * ``` */
        $subform<Subset extends Partial<T>>() {
            return sdk as unknown as FormState.Subform<
                // Creates a subform type from the `formState`
                // This can be passed to child components
                Pick<T, Extract<keyof Subset, keyof T>>
            >;
        },
    };

    return sdk;
}

/** Wraps `formState.validate()` so a failed submit is something the user can perceive.
 *
 * The target is the first error in *document order*: `formState.errors` follows the validation
 * config, not the layout, so only the DOM can answer which error the user should be taken to. */
export function useValidateWithFeedback(formState: { validate(): boolean }) {
    const { dispatchError } = useNotifications();

    return () => {
        if (formState.validate()) return true;

        /* The error elements are mounted by the commit `validate()` just scheduled. */
        requestAnimationFrame(() => {
            const firstError = document.querySelector('[data-form-error]');
            if (!firstError) return;

            /* The error renders below its input, so scrolling the error itself parks the label
             * and input behind the navbar. Scroll the whole field group instead.
             *
             * `useNavbarScrollOffset` keeps `scroll-padding-top` at the navbar height, which
             * `scrollIntoView` honours — no hand-computed offset needed. */
            (firstError.closest('[data-form-field]') ?? firstError).scrollIntoView({
                behavior: 'smooth',
                block: 'start',
            });

            if (firstError.textContent) dispatchError({ message: firstError.textContent });
        });

        return false;
    };
}

useFormState.createPreset =
    <T extends Record<string, unknown>>(
        validation: FormState.Validation<T> | ((i18n: I18n) => FormState.Validation<T>),
    ) =>
    (initialValues: NoInfer<T>): FormState<T> => {
        const i18n = useI18n();

        const cachedValidation: FormState.Validation<T> = useMemo(
            () => (typeof validation === 'function' ? validation(i18n) : validation),
            [i18n.locale],
        );

        return useFormState<T>(initialValues, cachedValidation);
    };

export type FormState<T extends Record<string, any>> = ts.Object.override<
    ReturnType<typeof _useFormState<T, any>>,
    {
        handleChange<K extends keyof T, V extends T[K] = T[K]>(
            key: K,
            value: React.SetStateAction<V>,
        ): void;
        /** Casts the this `FormState` to a `FormState.Subform<Subset>`.
         *
         * This is useful when you're composing multiple subform components that all receive the same `FromState` instance
         *
         * All subform components can own their own required types without having to know about the full form state.
         *
         * # Example
         * ```typescript
         * const CRUD_Page = props => {
         *    const form = useFormState<
         *        | Form1.FormValues
         *        | Form2.FormValues
         *        | Form3.FormValues
         *        | ...
         *    >({ ... })
         *
         *    // ...
         *
         *    return <>
         *        <Form1 form={form.$subform<From1.FormValues>()}
         *        <Form2 form={form.$subform<From2.FormValues>()}
         *        <Form3 form={form.$subform<From3.FormValues>()}
         *        { ... }
         *        <button onClick={onSubmit} />
         *    </>
         * }
         *
         * namespace Form1 {
         *    export type FormValues = { field1: string, ... };
         * }
         * const Form1: React.FC<{ form: FormState.Subform<Form1.FormValues> }> = props => <> ... </>
         *
         * ``` */
        $subform<Subset extends Partial<T>>(): FormState.Subform<
            // Creates a subform type from the `formState`
            // This can be passed to child components
            Pick<T, Extract<keyof Subset, keyof T>>
        >;
    }
>;

export namespace FormState {
    export type Subform<T extends Record<string, unknown>> = Omit<
        FormState<T>,
        'setValues' /* A subform can not update the whole form */
    >;

    export type Validation<T> = {
        [K in Extract<keyof T, string>]?: SingleOrArray<Validation.Entry<T, K>> | undefined;
    };
    export namespace Validation {
        export type Entry<T, K extends keyof T> = {
            validate: (value: T[K], allValues: T) => boolean;
            message: string;
        };
    }
}
